import { QuestionType } from '@features/event-registration/types';
import { FormFieldData, MemberFieldKey } from '@features/forms/types';
import {
  MEMBER_COUNTRY_FILTER_OPTIONS,
  MEMBER_STATE_FILTER_OPTIONS
} from '@features/member-management/helpers/memberAddressDropdownOptions';

export const BASIC_INFORMATION = {
  title: 'Basic information',
  subtitle: 'These fields are always included and cannot be removed.'
} as const;

export const MEMBER_FIELDS = {
  title: 'Member fields',
  subtitle:
    'Add fields to your form, when the student pays and is added as a member, their answers are automatically saved to the matching field on their member record — no manual entry needed.'
} as const;

export const CUSTOM_FIELDS = {
  title: 'Custom fields',
  subtitle: 'Add custom fields to your form'
} as const;

/** Custom field types allowed in the forms wizard (matches Phase 1 spec). */
export const FORM_CUSTOM_QUESTION_TYPES = [
  QuestionType.TEXT,
  QuestionType.TEXT_AREA,
  QuestionType.NUMBER,
  QuestionType.DROP_DOWN,
  QuestionType.SINGLE_CHOICE,
  QuestionType.DATE,
  QuestionType.FILE_UPLOAD,
  QuestionType.MULTIPLE_CHOICE
] as const;

export const MEMBER_FIELD_PICKER = {
  title: 'Add member field questions',
  infoText:
    'Fields selected here will be shown on the public form. When the student pays and is added as a member, their answers are automatically saved to the matching field on their member record — no manual entry needed.',
  personalGroupTitle: 'Personal information',
  addressGroupTitle: 'Address details',
  moreInfoGroupTitle: 'More info',
  mandatoryColumnLabel: 'Mandatory',
  saveLabel: 'Save',
  cancelLabel: 'Cancel',
  disabledCustomFieldTooltips: {
    catalogKey:
      'This field matches a standard member field. Add it from Personal information or Address details instead.',
    catalogLabel:
      'A member field with this label already exists. Use the matching field from the list above.',
    basicLabel:
      'This label is already used by a basic form field (Registration name or Mobile number).',
    duplicateCustomLabel:
      'Another custom member field uses the same label. Each label must be unique.'
  },
  disabledTooltipClassName: 'max-w-[250px] !whitespace-normal !text-left'
} as const;

export const LOCKED_FIELD_PLACEHOLDERS: Partial<Record<QuestionType, string>> =
  {
    [QuestionType.TEXT]: 'Text input',
    [QuestionType.MOBILE_NUMBER]: 'Enter phone number',
    [QuestionType.NUMBER]: 'Number input',
    [QuestionType.DATE]: 'DD/MM/YYYY'
  };

/** Disabled-input placeholders for selected member fields in the builder. */
export const MEMBER_FIELD_PREVIEW_PLACEHOLDERS: Partial<
  Record<QuestionType, string>
> = {
  [QuestionType.TEXT]: 'Text input',
  [QuestionType.EMAIL_ID]: 'Text input',
  [QuestionType.MOBILE_NUMBER]: 'Number input',
  [QuestionType.NUMBER]: 'Number input',
  [QuestionType.DATE]: 'DD/MM/YYYY',
  [QuestionType.DROP_DOWN]: 'Text input'
};

export type MemberFieldPickerGroup = 'personal' | 'address';

export interface MemberFieldCatalogEntry {
  key: MemberFieldKey;
  label: string;
  type: QuestionType;
  required: boolean;
  order: number;
  group: MemberFieldPickerGroup;
  options?: string[];
}

export const MEMBER_FIELD_CATALOG: MemberFieldCatalogEntry[] = [
  {
    key: 'alternate_mobile',
    label: 'Alternate number',
    type: QuestionType.MOBILE_NUMBER,
    required: false,
    order: 1,
    group: 'personal'
  },
  {
    key: 'email',
    label: 'Email',
    type: QuestionType.EMAIL_ID,
    required: false,
    order: 2,
    group: 'personal'
  },
  {
    key: 'dob',
    label: 'Date of birth',
    type: QuestionType.DATE,
    required: false,
    order: 3,
    group: 'personal'
  },
  {
    key: 'guardian_name',
    label: 'Guardian name',
    type: QuestionType.TEXT,
    required: false,
    order: 4,
    group: 'personal'
  },
  {
    key: 'address_line_1',
    label: 'Address line 1',
    type: QuestionType.TEXT,
    required: false,
    order: 5,
    group: 'address'
  },
  {
    key: 'address_line_2',
    label: 'Address line 2',
    type: QuestionType.TEXT,
    required: false,
    order: 6,
    group: 'address'
  },
  {
    key: 'country',
    label: 'Country',
    type: QuestionType.DROP_DOWN,
    required: false,
    order: 7,
    group: 'address'
  },
  {
    key: 'state',
    label: 'State',
    type: QuestionType.DROP_DOWN,
    required: false,
    order: 8,
    group: 'address'
  },
  {
    key: 'city',
    label: 'City',
    type: QuestionType.TEXT,
    required: false,
    order: 9,
    group: 'address'
  },
  {
    key: 'pincode',
    label: 'Pincode',
    type: QuestionType.NUMBER,
    required: false,
    order: 10,
    group: 'address'
  }
];

export const getMemberCatalogFieldOptions = (
  key: MemberFieldKey
): string[] | undefined => {
  if (key === 'country') {
    return MEMBER_COUNTRY_FILTER_OPTIONS.map((option) => option.label);
  }
  if (key === 'state') {
    return MEMBER_STATE_FILTER_OPTIONS.map((option) => option.label);
  }
  return undefined;
};

export const mapCatalogEntryToFormField = (
  entry: MemberFieldCatalogEntry,
  required = entry.required
): FormFieldData => {
  const catalogOptions =
    entry.options ?? getMemberCatalogFieldOptions(entry.key);

  return {
    id: `member-${entry.key}`,
    label: entry.label,
    type: entry.type,
    required,
    order: entry.order,
    fieldCategory: 'member',
    memberFieldKey: entry.key,
    syncToMember: true,
    ...(catalogOptions ? { options: catalogOptions } : {})
  };
};

export const getCatalogEntry = (key: MemberFieldKey) =>
  MEMBER_FIELD_CATALOG.find((entry) => entry.key === key);
